import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { School, LayoutDashboard, ListTodo, Home, ChevronLeft, BookOpen, Shield, MessageSquare, Settings as SettingsIcon } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useAuth } from '../../context/AuthContext';
import { getWorkbookId } from '../../services/plmsService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { FeedbackModal } from '../../components/shared/FeedbackModal';
import { UserInfoSection } from '../../components/layout/UserInfoSection';
import { employeeNamesService } from '../../services/employeeNames';

export const PLMSApp: React.FC = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSetup, setIsSetup] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { userEmail } = useAuth();
  const auth = getAuth();
  const { isAdmin } = useIsAdmin();

  usePageTitle('JLS PLMS', {
    'dashboard': 'Dashboard | JLS PLMS',
    'all-items': 'All Items | JLS PLMS',
    'master-lists': 'Master Lists | JLS PLMS',
    'settings': 'Settings | JLS PLMS',
    'admin': 'Admin | JLS PLMS'
  });

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    navigate('/');
  };

  useEffect(() => {
    const checkPLMSSetup = async () => {
      try {
        const user = auth.currentUser;
        if (!user?.email) {
          setIsLoading(false);
          return;
        }

        const workbookId = await getWorkbookId(user.email);
        if (!workbookId) {
          setShowDialog(true);
          return;
        }

        setIsSetup(true);
      } catch (error) {
        console.error('Error checking PLMS setup:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkPLMSSetup();
  }, [auth.currentUser]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  if (!isSetup) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900 justify-center items-center">
        <Dialog open={showDialog} onOpenChange={handleDialogClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>PLMS Not Set Up</DialogTitle>
              <DialogDescription>
                Your PLMS has not been set up yet. Please contact your manager to get access.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end">
              <Button onClick={handleDialogClose}>
                Return to App Hub
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <div className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white dark:bg-gray-800 border-r dark:border-gray-700 transition-all duration-300 ease-in-out relative flex flex-col`}>
        {/* Collapse Toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 z-10"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div className="border-b dark:border-gray-700 flex justify-center items-center">
          {isCollapsed ? (
            <School className="h-12 w-12 text-emerald-500 mb-1 mt-1" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <School className="h-20 w-20 text-emerald-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">JLS PLMS</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 px-4 text-center">Track and manage your personal learning journey</p>
            </div>
          )}
        </div>

        <nav className="flex-1 mt-4 overflow-y-auto">
          <Link
            to="/plms/dashboard"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/plms/dashboard') 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutDashboard className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Dashboard'}
          </Link>

          <Link
            to="/plms/all-items"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/plms/all-items') 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <ListTodo className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'All Items'}
          </Link>

          <Link
            to="/plms/master-lists"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/plms/master-lists') 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <BookOpen className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Master Lists'}
          </Link>

          {/* Give Feedback */}
          <div
            onClick={() => setIsFeedbackModalOpen(true)}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <MessageSquare className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Give Feedback'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Settings */}
          <Link
            to="/plms/settings"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/plms/settings') 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <SettingsIcon className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Settings'}
          </Link>

          {/* Admin Link */}
          {isAdmin && (
            <Link
              to="/plms/admin"
              className={`flex items-center px-4 py-2 text-sm ${
                isActive('/plms/admin') 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isCollapsed && 'Admin'}
            </Link>
          )}

          <Link
            to="/"
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Home className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Apps Hub'}
          </Link>
        </nav>

        {/* User Info Section */}
        <UserInfoSection 
          displayName={displayName} 
          isCollapsed={isCollapsed} 
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
        <Outlet />
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        appName="JLS PLMS"
      />
    </div>
  );
};
